import { ref, inject, computed } from "vue";
import { useRouter } from "vue-router";
import Toast from "@/plugins/Toast";
import { ToastType } from "@/types/toast";
import AuthRepository from "@/repositories/AuthRepository";
import { useDisplay } from "vuetify/lib/framework.mjs";

export default class RecoveryPasswordViewModel {
  // Datos del formulario
  public step = ref(1);
  public idsocio = ref("");
  public dni = ref("");
  public secretQuestion = ref("");
  public answer = ref("");
  public newPassword = ref("");
  public confirmPassword = ref("");

  // Visibilidad de inputs
  public isSecretAnswerVisible = ref(false);
  public isPwdVisible = ref(false);
  public isConfirmPwdVisible = ref(false);
  // Estado de carga
  public loading = ref(false); // Asegúrate de usar .value en la plantilla
  public loadingId = ref(false);
  public loadingData = ref(false);

  // Toast y router
  private toast: Toast;
  private router = useRouter();
  // Images
  logoImage: string;
  private display = useDisplay();
  public passwordRules = [
    (value: string) => !!value || "La contraseña es obligatoria.",
    (value: string) =>
      value.length >= 6 || "La contraseña debe tener al menos 6 caracteres.",
    (value: string) =>
      /[A-Z]/.test(value) ||
      "La contraseña debe contener al menos una letra mayúscula.",
    (value: string) =>
      /\d/.test(value) || "La contraseña debe contener al menos un número.",
  ];
  public confirmPasswordRules = [
    (value: string) => !!value || "Debes confirmar la contraseña.",
    (value: string) =>
      value === this.newPassword.value || "Las contraseñas no coinciden.",
  ];

  public stepLabels = computed(() => [
    { title: "Documento", step: 1 },
    { title: "Pregunta Secreta", step: 2 },
    { title: "Nueva Contraseña", step: 3 },
  ]);
  constructor() {
    this.toast = inject("toast") as Toast;

    this.logoImage = require("@/assets/images/logo.png");
  }
  toggleSecretAnswerVisibility = (): void => {
    this.isSecretAnswerVisible.value = !this.isSecretAnswerVisible.value;
  };
  togglePasswordVisibility = (): void => {
    this.isPwdVisible.value = !this.isPwdVisible.value;
  };
  toggleConfirmPasswordVisibility = (): void => {
    this.isConfirmPwdVisible.value = !this.isConfirmPwdVisible.value;
  };
  public resetForm = (): void => {
    this.step.value = 1;
    this.idsocio.value = "";
    this.dni.value = "";
    this.secretQuestion.value = "";
    this.answer.value = "";
    this.newPassword.value = "";
    this.confirmPassword.value = "";
    this.isSecretAnswerVisible.value = false;
    this.isPwdVisible.value = false;
    this.isConfirmPwdVisible.value = false;
    this.loading.value = false;
    this.loadingId.value = false;
    this.loadingData.value = false;
  };
  /**
   * Avanza al siguiente paso si los datos del paso actual son válidos.
   */
  public nextStep = async (): Promise<void> => {
    switch (this.step.value) {
      case 1:
        await this.confirmId();
        break;
      case 2:
        await this.confirmData();
        break;
      case 3:
        await this.changePassword();
        break;
      default:
        this.toast.showToast(
          "Paso no reconocido, por favor intenta nuevamente.",
          ToastType.Error
        );
    }
  };

  /**
   * Retrocede al paso anterior si no es el primero.
   */
  public previousStep = (): void => {
    if (this.step.value > 1) {
      this.step.value--;
    } else {
      this.toast.showToast("Ya estás en el primer paso.", ToastType.Info);
    }
  };
  /**
   * Valida el ID del socio en el primer paso
   */
  public confirmId = async (): Promise<void> => {
    if (!this.idsocio.value.trim()) {
      this.toast.showToast("El ID de socio es obligatorio.", ToastType.Info);
      return;
    }

    this.loadingId.value = true;

    try {
      const response = await AuthRepository.confirmId({
        dni: this.dni.value,
        idsocio: this.idsocio.value,
      });
      const { status, data } = response.data;

      if (status) {
        this.secretQuestion.value = data.secret_question;
        this.step.value = 2;
      } else {
        this.toast.showToast(
          "El Documento o  ID de socio es incorrecto.",
          ToastType.Error
        );
      }
    } catch (error) {
      console.error("Error al verificar el Documento o el ID de socio:", error);
      this.toast.showToast(
        "Error al verificar el Documento o el ID de socio.",
        ToastType.Error
      );
    } finally {
      this.loadingId.value = false;
    }
  };

  /**
   * Valida la respuesta secreta en el segundo paso
   */
  public confirmData = async (): Promise<void> => {
    if (!this.answer.value.trim()) {
      this.toast.showToast("La respuesta es obligatoria.", ToastType.Info);
      return;
    }

    this.loadingData.value = true;

    try {
      const response = await AuthRepository.confirmData({
        dni: this.dni.value,
        idsocio: this.idsocio.value,
        answer: this.answer.value,
      });
      const { status } = response.data;

      if (status) {
        // Inicializa datos para Step 3
        this.newPassword.value = "";
        this.confirmPassword.value = "";
        this.step.value = 3;
        this.loadingData.value = false;
      } else {
        this.toast.showToast(
          "La respuesta secreta es incorrecta.",
          ToastType.Error
        );
        this.loadingData.value = false;
      }
    } catch (error) {
      console.error("Error al validar la respuesta secreta:", error);
      this.toast.showToast(
        "Error al validar la respuesta secreta.",
        ToastType.Error
      );
      this.loadingData.value = false;
    }
  };

  /**
   * Cambia la contraseña en el tercer paso
   */
  public changePassword = async (): Promise<void> => {
    if (!this.validatePasswords()) {
      return;
    }

    this.loading.value = true;

    try {
      const response = await AuthRepository.changePassword({
        dni: this.dni.value,
        idsocio: this.idsocio.value,
        new_password: this.newPassword.value,
        new_password_confirmation: this.confirmPassword.value,
      });

      const { status } = response.data;

      if (status) {
        this.toast.showToast(
          "Contraseña cambiada con éxito.",
          ToastType.Success
        );
        this.resetForm();
      } else {
        this.toast.showToast(
          "No se pudo cambiar la contraseña.",
          ToastType.Error
        );
      }
    } catch (error) {
      console.error("Error al cambiar la contraseña:", error);
      this.toast.showToast("Error al cambiar la contraseña.", ToastType.Error);
    } finally {
      this.loading.value = false;
    }
  };

  /**
   * Valida las contraseñas del tercer paso
   */
  private validatePasswords = (): boolean => {
    if (!this.newPassword.value || !this.confirmPassword.value) {
      this.toast.showToast(
        "Todos los campos de contraseña son obligatorios.",
        ToastType.Info
      );
      return false;
    }

    if (this.newPassword.value !== this.confirmPassword.value) {
      this.toast.showToast("Las contraseñas no coinciden.", ToastType.Error);
      return false;
    }

    if (this.newPassword.value.length < 6) {
      this.toast.showToast(
        "La contraseña debe tener al menos 6 caracteres.",
        ToastType.Error
      );
      return false;
    }

    if (!/^(?=.*[A-Z])(?=.*\d)/.test(this.newPassword.value)) {
      this.toast.showToast(
        "La contraseña debe tener al menos una letra mayúscula y un número.",
        ToastType.Error
      );
      return false;
    }

    return true;
  };
}
